import React, { useState } from 'react';

import { cn } from '@modernloop/ai-elements';
import { useTranslation } from '@modernloop/i18n';

/**
 * Playful empty-thread greetings. These are English-only wordplay on the ModernLoop name, so
 * they deliberately bypass i18n; non-English locales get the plain localized greeting instead.
 */
export const AGENT_GREETING_PHRASES = [
  'Another day, another loop',
  'Back in the loop',
  'Complete the loop',
  'Consider it looped',
  'Consider me looped in',
  'Consider the loop handled',
  'Enter the loop',
  'Full loop ahead',
  'I am once again asking to be looped in',
  "I'll keep everyone in the loop",
  "It's loopin' time",
  'Keep me in the loop',
  "Let's close the loop",
  "Let's get you in the loop",
  "Let's make a loop",
  'Live. Laugh. Loop.',
  'Loop me in',
  'Loop mode: on',
  'Loop! There it is!',
  'Looping you in',
  'One less loop for you to close',
  'Oops, all loops',
  'Stay in the loop',
  'Trust the loop',
  'Welcome to the loop',
  "You're in the loop now",
  'Your modern helping hand',
  'Your personal loop closer',
] as const;

/** Picks the greeting phrase for one page load from a random draw in [0, 1). */
export const pickAgentGreetingPhrase = (randomDraw: number): string =>
  AGENT_GREETING_PHRASES[
    Math.min(Math.floor(randomDraw * AGENT_GREETING_PHRASES.length), AGENT_GREETING_PHRASES.length - 1)
  ];

/** True for every English-based locale, including en-GB and the en-sloth test locale. */
export const isEnglishLanguage = (language: string): boolean => language === 'en' || language.startsWith('en-');

type AgentGreetingProps = {
  className?: string;
  language?: string;
  translate?: (key: string) => string;
};

/**
 * Large National 2 headline over the empty Agent composer.
 *
 * The phrase is drawn once per mount, so it stays stable while the user types and only
 * changes on the next load of the empty canvas.
 */
const AgentGreeting: React.FC<AgentGreetingProps> = ({ className, language, translate }) => {
  const { t, i18n } = useTranslation();
  const [phrase] = useState(() => pickAgentGreetingPhrase(Math.random()));
  const activeLanguage = language ?? i18n.language;
  const activeTranslate = translate ?? t;

  return (
    <h1
      className={cn('agent-greeting-font mb-8 text-center text-4xl text-foreground sm:text-5xl', className)}
      data-testid="agent-greeting"
    >
      {isEnglishLanguage(activeLanguage) ? phrase : activeTranslate('hiring-dashboard-ai-assistant:agent.greeting')}
    </h1>
  );
};

export default AgentGreeting;
